import type { ReactNode } from 'react';
import {
  ImageIcon,
  Info,
  LayoutTemplate,
  Palette,
  Sparkles,
  Type,
  UserRound,
} from 'lucide-react';
import PowerProfileDialogMock from '@/components/help-center/powers/PowerProfileDialogMock';
import { RichTextEditor } from '@/components/painel/cms/RichTextEditor';
import { painelInputClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import { PstyleColorField } from '@/components/painel/powers/PstyleColorField';
import {
  painelInsetCardClass,
  painelSelectContentClass,
  painelSwitchClass,
} from '@/components/painel/painel-styles';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  pstyleCmsConfigToDialogMock,
  type PowerPstyleEditorInput,
  type PowerPstyleSectionCopy,
} from '@/lib/power-pstyle-cms';
import { PSTYLE_DIALOG_DISPLAY_NAME, PSTYLE_DIALOG_USER_ID } from '@/lib/power-pstyle-themes';
import { cn } from '@/lib/utils';

type PowerPstyleEditorProps = {
  value: PowerPstyleEditorInput;
  onChange: (value: PowerPstyleEditorInput) => void;
  avatarFallback?: string;
};

function EditorPanel({
  icon: Icon,
  title,
  description,
  children,
  accentClass = 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/30',
}: {
  icon: typeof Palette;
  title: string;
  description?: string;
  children: ReactNode;
  accentClass?: string;
}) {
  return (
    <section className={cn(painelInsetCardClass, 'overflow-hidden')}>
      <header className="flex items-start gap-3 border-b border-white/10 bg-white/[0.02] px-4 py-3.5 sm:px-5">
        <span
          className={cn(
            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1',
            accentClass
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="font-poppins text-sm font-semibold text-slate-100">{title}</h3>
          {description ? (
            <p className="mt-0.5 font-inter text-xs leading-relaxed text-slate-500">{description}</p>
          ) : null}
        </div>
      </header>
      <div className="space-y-4 px-4 py-4 sm:px-5">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={htmlFor} className="font-poppins text-[13px] text-slate-300">
          {label}
        </Label>
        {hint ? <span className="font-inter text-[11px] text-slate-500">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

export function PowerPstyleEditor({ value, onChange, avatarFallback }: PowerPstyleEditorProps) {
  const patchConfig = (partial: Partial<PowerPstyleEditorInput['config']>) => {
    onChange({
      ...value,
      config: { ...value.config, ...partial },
    });
  };

  const patchSectionCopy = (partial: Partial<PowerPstyleSectionCopy>) => {
    patchConfig({
      sectionCopy: {
        ...value.config.sectionCopy,
        title: value.config.sectionCopy?.title ?? '',
        requirement: value.config.sectionCopy?.requirement ?? '',
        intro: value.config.sectionCopy?.intro ?? '',
        body: value.config.sectionCopy?.body ?? '',
        tip: value.config.sectionCopy?.tip ?? '',
        ...partial,
      },
    });
  };

  const copy = value.config.sectionCopy ?? {
    title: '',
    requirement: '',
    intro: '',
    body: '',
    tip: '',
  };

  const previewMock = pstyleCmsConfigToDialogMock(value.config, avatarFallback);
  const hasOverlayImage = Boolean(value.config.overlayImageUrl.trim());

  return (
    <div className="space-y-5">
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl border border-emerald-400/20',
          'bg-gradient-to-br from-emerald-500/[0.12] via-white/[0.03] to-transparent p-4 sm:p-5'
        )}
      >
        <div className="flex items-start gap-3">
          <Switch
            id="pstyle-customize"
            checked={value.customizeEnabled}
            onCheckedChange={(customizeEnabled) => onChange({ ...value, customizeEnabled })}
            className={cn(painelSwitchClass, 'mt-1 shrink-0')}
          />
          <div className="min-w-0 flex-1 space-y-1">
            <Label htmlFor="pstyle-customize" className="font-poppins text-base text-white">
              Personalizar Pstyle
            </Label>
            <p className="font-inter text-sm leading-relaxed text-slate-400">
              Ative para editar o texto da página pública e a pré-visualização do diálogo de perfil.
              Desligado = tema predefinido do power (se existir).
            </p>
            <p className="font-inter text-xs text-slate-500">
              Nome fixo na pré-visualização:{' '}
              <span className="text-slate-400">
                {PSTYLE_DIALOG_DISPLAY_NAME} ({PSTYLE_DIALOG_USER_ID})
              </span>
            </p>
          </div>
          <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-400/30 sm:flex">
            <Sparkles className="h-5 w-5 text-emerald-300" aria-hidden />
          </span>
        </div>
      </div>

      {!value.customizeEnabled ? (
        <div className={cn(painelInsetCardClass, 'px-5 py-6 text-center')}>
          <p className="font-inter text-sm text-slate-400">
            Personalização desativada. Ative o interruptor acima para editar o conteúdo e o tema.
          </p>
        </div>
      ) : (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(300px,380px)] xl:items-start">
          <div className="space-y-4">
            <EditorPanel
              icon={Type}
              title="Texto da página pública"
              description="Coluna esquerda da secção Pstyle — título, requisitos e instruções."
              accentClass="bg-sky-500/15 text-sky-300 ring-sky-400/30"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Título" htmlFor="pstyle-copy-title">
                  <Input
                    id="pstyle-copy-title"
                    value={copy.title}
                    onChange={(e) => patchSectionCopy({ title: e.target.value })}
                    className={painelInputClass}
                    placeholder="Tema Pstyle · Astrox"
                  />
                </Field>
                <Field label="Subtítulo / requisito" htmlFor="pstyle-copy-req" hint="opcional">
                  <Input
                    id="pstyle-copy-req"
                    value={copy.requirement}
                    onChange={(e) => patchSectionCopy({ requirement: e.target.value })}
                    className={painelInputClass}
                    placeholder="Power Pstyle + Astrox"
                  />
                </Field>
              </div>
              <Field label="Texto" htmlFor="pstyle-copy-intro">
                <Textarea
                  id="pstyle-copy-intro"
                  value={copy.intro}
                  onChange={(e) => patchSectionCopy({ intro: e.target.value, body: '' })}
                  className={cn(painelTextareaClass, 'min-h-[140px]')}
                  placeholder="Descrição do Pstyle na página pública…"
                />
              </Field>
              <div className="space-y-2">
                <Label className="font-poppins text-[13px] text-slate-300">Dica (caixa verde)</Label>
                <p className="font-inter text-[11px] text-slate-500">
                  Aparece na página pública como a caixa verde de instruções.
                </p>
                <RichTextEditor
                  value={copy.tip}
                  onChange={(tip) => patchSectionCopy({ tip })}
                  toolbar="extended"
                  tone="tip"
                  editorMinHeightClass="min-h-[120px]"
                  resetKey={`pstyle-tip-${value.customizeEnabled}`}
                />
              </div>
            </EditorPanel>

            <EditorPanel
              icon={Palette}
              title="Header e botões"
              description="Degradê ou cor sólida no topo do diálogo e nos botões de ação."
            >
              <PstyleColorField
                label="Cor / degradê"
                value={value.config.gradient}
                onChange={(gradient) => patchConfig({ gradient })}
                placeholder="linear-gradient(225deg, #633775, #361e40)"
              />
              <Field label="Padrão extra nos botões">
                <Select
                  value={value.config.buttonPattern}
                  onValueChange={(v) =>
                    patchConfig({
                      buttonPattern: v as PowerPstyleEditorInput['config']['buttonPattern'],
                    })
                  }
                >
                  <SelectTrigger className={painelInputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={painelSelectContentClass}>
                    <SelectItem value="none">Nenhum</SelectItem>
                    <SelectItem value="spiderweb">Teia (Morgana)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </EditorPanel>

            <EditorPanel
              icon={ImageIcon}
              title="Imagem nos botões"
              description="Opcional. Aparece só na área dos botões, por cima do degradê."
              accentClass="bg-violet-500/15 text-violet-300 ring-violet-400/30"
            >
              <Field label="URL da imagem" htmlFor="pstyle-overlay-url" hint="opcional">
                <Input
                  id="pstyle-overlay-url"
                  value={value.config.overlayImageUrl}
                  onChange={(e) => patchConfig({ overlayImageUrl: e.target.value })}
                  className={painelInputClass}
                  placeholder="https://xat.com/content/web/.../astrox.png"
                />
              </Field>
              {hasOverlayImage ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Opacidade" htmlFor="pstyle-overlay-opacity">
                    <div className="space-y-2">
                      <input
                        id="pstyle-overlay-opacity"
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={value.config.overlayOpacity}
                        onChange={(e) =>
                          patchConfig({ overlayOpacity: Number(e.target.value) || 0 })
                        }
                        className="w-full accent-emerald-400"
                      />
                      <p className="font-mono text-[11px] text-slate-500">
                        {value.config.overlayOpacity.toFixed(2)}
                      </p>
                    </div>
                  </Field>
                  <Field label="Escala" htmlFor="pstyle-overlay-size">
                    <Input
                      id="pstyle-overlay-size"
                      value={value.config.overlayBackgroundSize}
                      onChange={(e) => patchConfig({ overlayBackgroundSize: e.target.value })}
                      className={painelInputClass}
                      placeholder="280px auto"
                    />
                  </Field>
                </div>
              ) : (
                <p className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-3 py-2.5 font-inter text-xs text-slate-500">
                  Preencha a URL para ajustar opacidade e escala.
                </p>
              )}
            </EditorPanel>

            <EditorPanel
              icon={LayoutTemplate}
              title="Badges e status"
              description="Etiquetas de nome e estado no diálogo."
              accentClass="bg-amber-500/15 text-amber-300 ring-amber-400/30"
            >
              <PstyleColorField
                label="Fundo das badges"
                value={value.config.infoBoxBackground}
                onChange={(infoBoxBackground) => patchConfig({ infoBoxBackground })}
                placeholder="rgba(120, 72, 140, 0.72)"
                allowAlpha
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Texto de status" htmlFor="pstyle-status-text">
                  <Input
                    id="pstyle-status-text"
                    value={value.config.statusText}
                    onChange={(e) => patchConfig({ statusText: e.target.value })}
                    className={painelInputClass}
                    placeholder="lost in space"
                  />
                </Field>
                <PstyleColorField
                  label="Cor do texto de status"
                  value={value.config.statusTextColor}
                  onChange={(statusTextColor) => patchConfig({ statusTextColor })}
                  placeholder="#361e40"
                />
              </div>
            </EditorPanel>

            <EditorPanel
              icon={UserRound}
              title="Avatar"
              description="Smilie na pré-visualização do diálogo."
              accentClass="bg-cyan-500/15 text-cyan-300 ring-cyan-400/30"
            >
              <Field label="Código do smilie" htmlFor="pstyle-avatar">
                <Input
                  id="pstyle-avatar"
                  value={value.config.avatarCode}
                  onChange={(e) => patchConfig({ avatarCode: e.target.value })}
                  className={cn(painelInputClass, 'font-mono')}
                  placeholder="astshine"
                />
              </Field>
            </EditorPanel>
          </div>

          <aside className="xl:sticky xl:top-6">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] shadow-xl shadow-black/30">
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
                <Info className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Pré-visualização
                </p>
              </div>
              <div className="bg-white p-3 sm:p-4">
                <PowerProfileDialogMock mock={previewMock} />
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
